import type { ReactNode } from "react";
import InteractiveElementIllustration from "@/components/interactive/InteractiveElementIllustration";
import type { InteractiveIllustrationKind } from "@/lib/interactiveIllustration";

export type TaskStatusBadge = "in_progress" | "completed" | "finished";

interface InteractiveElementCardProps {
  illustrationKind: InteractiveIllustrationKind;
  title: string;
  titleId: string;
  intro?: ReactNode;
  action: ReactNode;
  statusBadge?: TaskStatusBadge | null;
  /**
   * "beside" keeps intro and CTA in the column next to the illustration
   * (start card). "below" stacks a recap under the illustration + title,
   * matching the inline quiz summary.
   */
  bodyPlacement?: "beside" | "below";
}

const INFO_BADGE_CLASS =
  "border-info-border bg-info-light text-info-foreground";

const STATUS_BADGE: Record<
  TaskStatusBadge,
  { label: string; className: string }
> = {
  in_progress: {
    label: "Angefangen",
    className: INFO_BADGE_CLASS,
  },
  // A finished modal stays a start card, so the pill matches Angefangen.
  finished: {
    label: "Abgeschlossen",
    className: INFO_BADGE_CLASS,
  },
  completed: {
    label: "Abgeschlossen",
    className: "border-success-border bg-success-light text-success-foreground",
  },
};

/**
 * Card for interactive elements — illustration + heading, with the CTA beside
 * the art on start cards and the recap stacked below on summaries.
 */
const InteractiveElementCard = ({
  illustrationKind,
  title,
  titleId,
  intro,
  action,
  statusBadge = null,
  bodyPlacement = "beside",
}: InteractiveElementCardProps) => {
  const badge = statusBadge ? STATUS_BADGE[statusBadge] : null;

  const renderStatusBadge = () => {
    if (!badge) return null;

    return (
      <span
        className={`inline-flex items-center rounded-full border px-2.5 py-0.5 font-semibold ${badge.className}`}
      >
        {badge.label}
      </span>
    );
  };

  const renderIllustration = () => (
    <InteractiveElementIllustration kind={illustrationKind} />
  );

  const renderTitleRow = () => (
    <div className="flex min-w-0 flex-col items-start gap-2">
      <h2 id={titleId}>{title}</h2>
      {renderStatusBadge()}
    </div>
  );

  const renderStartLayout = () => (
    <div
      data-slot="interactive-element-layout"
      className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-6"
    >
      {renderIllustration()}
      <div className="flex min-w-0 flex-col items-start gap-4">
        {renderTitleRow()}
        {intro}
        {action}
      </div>
    </div>
  );

  const renderRecapLayout = () => (
    <div data-slot="interactive-element-layout" className="flex flex-col gap-4">
      <div
        data-slot="interactive-element-header"
        className="flex items-center gap-6"
      >
        {renderIllustration()}
        {renderTitleRow()}
      </div>
      <div className="min-w-0 w-full">{action}</div>
    </div>
  );

  if (bodyPlacement === "below") return renderRecapLayout();
  return renderStartLayout();
};

export default InteractiveElementCard;
